import axios from 'axios';
import { authApiUrl, authLoginUrl } from './Config';

const appSessionStorageKey = 'lexikaizenAppSessionToken';

export const getAppSessionToken = () => localStorage.getItem(appSessionStorageKey) || '';

const setAppSessionToken = (token) => {
    if (token) {
        localStorage.setItem(appSessionStorageKey, token);
    }
};

export const fetchCurrentUser = async () => {
    try {
        const appSessionToken = getAppSessionToken();
        const response = await axios.get(authApiUrl, {
            withCredentials: true,
            headers: appSessionToken ? {
                'X-App-Session-Token': appSessionToken,
            } : {},
            validateStatus: (status) => status < 500,
        });

        if (response.data?.appSessionToken) {
            setAppSessionToken(response.data.appSessionToken);
        }

        if (response.data?.tariff) {
            return response.data;
        }

        return {
            authenticated: false,
            loginUrl: response.data?.loginUrl || authLoginUrl,
            reason: response.data?.reason,
        };
    } catch (error) {
        return {
            authenticated: false,
            loginUrl: authLoginUrl,
            tariff: null,
        };
    }
};

export const redirectToLogin = (loginUrl = authLoginUrl) => {
    const target = new URL(loginUrl, window.location.origin);
    target.searchParams.set('returnTo', window.location.href);
    window.location.href = target.toString();
};

export const getUnlockedLessonIds = (auth) => {
    const lessonIds = auth?.tariff?.lessonIds;

    if (!Array.isArray(lessonIds)) {
        return new Set();
    }

    return new Set(lessonIds.map((lessonId) => String(lessonId)));
};

export const getUnlockedLessonKeys = (auth) => {
    const lessonKeys = auth?.tariff?.lessonKeys;

    if (!Array.isArray(lessonKeys)) {
        return new Set();
    }

    return new Set(lessonKeys.map((lessonKey) => String(lessonKey)));
};

export const getUnlockedLanguageCodes = (auth) => {
    const languageCodes = auth?.tariff?.effectiveLanguageCodes || auth?.tariff?.languageCodes;

    if (!Array.isArray(languageCodes)) {
        return new Set();
    }

    return new Set(languageCodes.map((languageCode) => String(languageCode)));
};

export const getConfiguredLanguageCodes = (auth) => {
    const languageCodes = auth?.tariff?.languageCodes;

    if (!Array.isArray(languageCodes)) {
        return new Set();
    }

    return new Set(languageCodes.map((languageCode) => String(languageCode)));
};

export const getUnlockedTopicKeys = (auth) => {
    const topicKeys = auth?.tariff?.effectiveTopicKeys || auth?.tariff?.topicKeys;

    if (!Array.isArray(topicKeys)) {
        return new Set();
    }

    return new Set(topicKeys.map((topicKey) => String(topicKey)));
};

export const getConfiguredTopicKeys = (auth) => {
    const topicKeys = auth?.tariff?.topicKeys;

    if (!Array.isArray(topicKeys)) {
        return new Set();
    }

    return new Set(topicKeys.map((topicKey) => String(topicKey)));
};

export const isTariffAccessAll = (auth) => auth?.tariff?.accessAll === true;

export const isLanguageUnlocked = (language, auth) => {
    const languageCode = language?.code || language;

    if (!languageCode) {
        return false;
    }

    if (isTariffAccessAll(auth)) {
        return true;
    }

    return getUnlockedLanguageCodes(auth).has(String(languageCode));
};

export const isLanguageFullyUnlocked = (language, auth) => {
    const languageCode = language?.code || language;

    if (!languageCode) {
        return false;
    }

    if (isTariffAccessAll(auth)) {
        return true;
    }

    return getConfiguredLanguageCodes(auth).has(String(languageCode));
};

export const isTopicFullyUnlocked = (group, language, auth) => {
    const languageCode = language?.code || language;

    if (!group?.id || !languageCode) {
        return false;
    }

    if (isLanguageFullyUnlocked(languageCode, auth)) {
        return true;
    }

    return getConfiguredTopicKeys(auth).has(`${languageCode}:${group.id}`);
};

export const isGroupUnlocked = (group, language, auth) => {
    const languageCode = language?.code || language;

    if (!group?.id || !languageCode) {
        return false;
    }

    if (isTopicFullyUnlocked(group, languageCode, auth)) {
        return true;
    }

    return getUnlockedTopicKeys(auth).has(`${languageCode}:${group.id}`);
};

export const isAreaUnlocked = (area, auth, language = null, group = null) => {
    if (!area?.id) {
        return false;
    }

    if (isTariffAccessAll(auth)) {
        return true;
    }

    const languageCode = language?.code || language;

    if (languageCode && isLanguageFullyUnlocked(languageCode, auth)) {
        return true;
    }

    if (languageCode && group?.id && isTopicFullyUnlocked(group, languageCode, auth)) {
        return true;
    }

    if (languageCode && getUnlockedLessonKeys(auth).has(`${languageCode}:${area.id}`)) {
        return true;
    }

    return getUnlockedLessonIds(auth).has(String(area.id));
};

// The tariff merges public access and purchased access; the license is separate.
export const isAreaFree = (area, auth, language = null, group = null) => {
    if (!isAreaUnlocked(area, auth, language, group)) return false;
    const license = auth?.license;
    const coveredByLicense = auth?.authorized === true && license?.active === true
        && isAreaUnlocked(area, { tariff: license }, language, group);
    return !coveredByLicense;
};
